import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from './constants';
import { authService } from './services/authService';

const TagManagement = () => {
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState({ name: '', color: '#007bff', description: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingTag, setEditingTag] = useState(null);

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      console.log('Fetching tags from:', `${API_BASE_URL}/tags`);
      const response = await authService.apiFetch(`${API_BASE_URL}/tags`, {
        headers: authService.getAuthHeaders()
      });
      console.log('Fetch tags response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Tags fetched successfully:', data);
        console.log('First tag structure:', data[0]); // Debug first tag structure
        setTags(data);
        setError('');
      } else {
        console.error('Failed to fetch tags, status:', response.status);
        setError('Failed to fetch tags');
      }
    } catch (error) {
      console.error('Network error fetching tags:', error);
      setError(`Failed to fetch tags: ${error.message}`);
    }
  };

  const handleCreateTag = async (e) => {
    e.preventDefault();
    if (!newTag.name.trim()) {
      setError('Tag name is required');
      return;
    }

    // Check if user is authenticated
    if (!authService.isUserAuthenticated()) {
      setError('You must be logged in to create tags. Please log in and try again.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('Creating tag with URL:', `${API_BASE_URL}/tags`);
      console.log('Tag data:', newTag);
      console.log('Auth headers:', authService.getAuthHeaders());

      const response = await authService.apiFetch(`${API_BASE_URL}/tags`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(newTag),
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (response.ok) {
        const result = await response.json();
        console.log('Tag created successfully:', result);
        setNewTag({ name: '', color: '#007bff', description: '' });
        fetchTags();
      } else if (response.status === 401 || response.status === 403) {
        // Token is invalid or expired
        const errorData = await response.json();
        console.error('Authentication error:', errorData);
        authService.clearSession();
        setError('Your session has expired. Please log in again to create tags.');
      } else {
        const errorData = await response.json();
        console.error('Error creating tag:', errorData);
        setError(errorData.message || errorData.error || 'Failed to create tag');
      }
    } catch (error) {
      console.error('Network error creating tag:', error);
      setError(`Network error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTag = async (tagId, updatedData) => {
    try {
      const response = await authService.apiFetch(`${API_BASE_URL}/tags/${tagId}`, {
        method: 'PUT',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(updatedData),
      });

      if (response.ok) {
        fetchTags();
        setEditingTag(null);
      } else if (response.status === 401 || response.status === 403) {
        // Token is invalid or expired
        const errorData = await response.json();
        console.error('Authentication error:', errorData);
        authService.clearSession();
        setError('Your session has expired. Please log in again to update tags.');
      } else {
        const errorData = await response.json();
        setError(errorData.message || errorData.error || 'Failed to update tag');
      }
    } catch (error) {
      setError('Failed to update tag');
    }
  };

  const handleDeleteTag = async (tagId) => {
    if (!window.confirm('Are you sure you want to delete this tag? It will be removed from all test results.')) {
      return;
    }

    try {
      const response = await authService.apiFetch(`${API_BASE_URL}/tags/${tagId}`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders(),
      });

      if (response.ok) {
        fetchTags();
      } else if (response.status === 401 || response.status === 403) {
        // Token is invalid or expired
        const errorData = await response.json();
        console.error('Authentication error:', errorData);
        authService.clearSession();
        setError('Your session has expired. Please log in again to delete tags.');
      } else {
        setError('Failed to delete tag');
      }
    } catch (error) {
      setError('Failed to delete tag');
    }
  };

  const startEditing = (tag) => {
    setEditingTag({
      ...tag,
      originalId: tag._id
    });
  };

  const cancelEditing = () => {
    setEditingTag(null);
  };

  const saveEdit = () => {
    if (editingTag && editingTag.name.trim()) {
      const { originalId, _id, createdAt, updatedAt, ...updateData } = editingTag;
      handleUpdateTag(originalId, updateData);
    }
  };

  return (
    <div style={{
      maxWidth: '1000px',
      margin: '0 auto',
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Header Section */}
      <div style={{
        textAlign: 'center',
        marginBottom: '40px',
        borderBottom: '2px solid #f0f0f0',
        paddingBottom: '20px'
      }}>
        <h1 style={{
          color: '#2c3e50',
          fontSize: '28px',
          fontWeight: '600',
          margin: '0 0 10px 0'
        }}>
          Tag Management
        </h1>
        <p style={{
          color: '#7f8c8d',
          fontSize: '16px',
          margin: '0',
          maxWidth: '500px',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          Create and manage tags to organize your test results efficiently
        </p>
      </div>

      {error && (
        <div style={{
          color: '#e74c3c',
          marginBottom: '30px',
          padding: '15px 20px',
          backgroundColor: '#fdf2f2',
          border: '1px solid #f8d7da',
          borderRadius: '8px',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          boxShadow: '0 2px 4px rgba(231, 76, 60, 0.1)'
        }}>
          <span style={{ marginRight: '8px', fontSize: '16px' }}>⚠️</span>
          {error}
        </div>
      )}

      {/* Create New Tag Form */}
      <div style={{
        backgroundColor: '#ffffff',
        padding: '30px 40px',
        borderRadius: '12px',
        marginBottom: '40px',
        border: '1px solid #e9ecef',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '0',
          left: '0',
          right: '0',
          height: '4px',
          background: 'linear-gradient(90deg, #007bff, #0056b3)',
          borderRadius: '12px 12px 0 0'
        }}></div>

        <h3 style={{
          color: '#2c3e50',
          fontSize: '20px',
          fontWeight: '600',
          marginBottom: '25px',
          display: 'flex',
          alignItems: 'center'
        }}>
          <span style={{ marginRight: '10px', fontSize: '18px' }}>🏷️</span>
          Create New Tag
        </h3>

        <form onSubmit={handleCreateTag}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 100px 1fr',
            gap: '20px',
            alignItems: 'start',
            marginBottom: '30px'
          }}>
            {/* Tag Name */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#495057',
                fontSize: '14px'
              }}>
                Tag Name *
              </label>
              <input
                type="text"
                value={newTag.name}
                onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
                placeholder="e.g., High Priority, Abnormal, Follow-up"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e9ecef',
                  borderRadius: '8px',
                  fontSize: '14px',
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                  outline: 'none',
                  backgroundColor: '#ffffff'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#007bff';
                  e.target.style.boxShadow = '0 0 0 3px rgba(0, 123, 255, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e9ecef';
                  e.target.style.boxShadow = 'none';
                }}
                required
              />
            </div>

            {/* Color Picker */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#495057',
                fontSize: '14px',
                marginLeft: '-60px'
              }}>
                Color
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="color"
                  value={newTag.color}
                  onChange={(e) => setNewTag({ ...newTag, color: e.target.value })}
                  style={{
                    width: '100%',
                    height: '48px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundColor: newTag.color
                  }}
                />
                <div style={{
                  position: 'absolute',
                  bottom: '-20px',
                  left: '0',
                  fontSize: '11px',
                  color: '#6c757d',
                  fontFamily: 'monospace'
                }}>
                  {newTag.color}
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#495057',
                fontSize: '14px'
              }}>
                Description
              </label>
              <input
                type="text"
                value={newTag.description}
                onChange={(e) => setNewTag({ ...newTag, description: e.target.value })}
                placeholder="Optional description or notes"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e9ecef',
                  borderRadius: '8px',
                  fontSize: '14px',
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                  outline: 'none',
                  backgroundColor: '#ffffff'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#007bff';
                  e.target.style.boxShadow = '0 0 0 3px rgba(0, 123, 255, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e9ecef';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          </div>


          <button
            type="submit"
            disabled={loading}
            style={{
              backgroundColor: loading ? '#6c757d' : '#007bff',
              color: 'white',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              fontWeight: '500',
              transition: 'all 0.2s ease',
              boxShadow: loading ? 'none' : '0 2px 4px rgba(0, 123, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseOver={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = '#0056b3';
              }
            }}
            onMouseOut={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = '#007bff';
              }
            }}
          >
            {loading ? (
              <>
                <span style={{
                  display: 'inline-block',
                  width: '14px',
                  height: '14px',
                  border: '2px solid #ffffff',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></span>
                Creating...
              </>
            ) : (
              <>
                <span>✨</span>
                Create Tag
              </>
            )}
          </button>
        </form>
      </div>

      {/* Existing Tags List */}
      <div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '25px',
          paddingBottom: '15px',
          borderBottom: '2px solid #f0f0f0'
        }}>
          <h3 style={{
            color: '#2c3e50',
            fontSize: '20px',
            fontWeight: '600',
            margin: '0',
            display: 'flex',
            alignItems: 'center'
          }}>
            <span style={{ marginRight: '10px', fontSize: '18px' }}>📋</span>
            Existing Tags
          </h3>
          <div style={{
            backgroundColor: '#e9ecef',
            color: '#495057',
            padding: '6px 12px',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: '600'
          }}>
            {tags.length} {tags.length === 1 ? 'tag' : 'tags'}
          </div>
        </div>

        {tags.length === 0 ? (
          <p style={{ color: '#666', fontStyle: 'italic' }}>
            No tags created yet. Create your first tag above!
          </p>
        ) : (
          <div style={{ display: 'grid', gap: '10px' }}>
            {tags.map((tag) => (
              <div key={tag._id} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '15px',
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '6px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                {editingTag && editingTag.originalId === tag._id ? (
                  // Edit mode
                  <div style={{ flex: '1', display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={editingTag.name}
                      onChange={(e) => setEditingTag({ ...editingTag, name: e.target.value })}
                      style={{
                        padding: '6px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                    <input
                      type="color"
                      value={editingTag.color}
                      onChange={(e) => setEditingTag({ ...editingTag, color: e.target.value })}
                      style={{
                        width: '40px',
                        height: '32px',
                        border: '1px solid #ccc',
                        borderRadius: '4px'
                      }}
                    />
                    <input
                      type="text"
                      value={editingTag.description || ''}
                      onChange={(e) => setEditingTag({ ...editingTag, description: e.target.value })}
                      placeholder="Description"
                      style={{
                        flex: '1',
                        padding: '6px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                    <div>
                      <button
                        onClick={saveEdit}
                        style={{
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '11px',
                          marginRight: '5px'
                        }}
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelEditing}
                        style={{
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '11px'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  // Display mode
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', flex: '1' }}>
                      <span style={{
                        backgroundColor: tag.color,
                        color: 'white',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        marginRight: '15px'
                      }}>
                        {tag.name}
                      </span>
                      {tag.description && (
                        <span style={{
                          fontSize: '14px',
                          color: '#666',
                          fontStyle: 'italic'
                        }}>
                          {tag.description}
                        </span>
                      )}
                      <div style={{
                        fontSize: '12px',
                        color: '#999',
                        marginLeft: 'auto',
                        marginRight: '15px',
                        textAlign: 'right'
                      }}>
                        <div>Created: {new Date(tag.createdAt).toLocaleDateString()}</div>
                        {tag.createdBy && (
                          <div style={{ fontStyle: 'italic', fontSize: '11px' }}>
                            by: {tag.createdBy}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => startEditing(tag)}
                        style={{
                          backgroundColor: '#ffc107',
                          color: 'black',
                          border: 'none',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '11px'
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteTag(tag._id)}
                        style={{
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '11px'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add CSS animation for loading spinner */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default TagManagement;